import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, AlertCircle } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_c03b2305-10b0-43c0-8499-c191377bc941/artifacts/02yyq1u7_EconomicClubofSa.png';

export default function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    const { error: authError, suspended } = await signIn(email, password);
    setLoading(false);

    if (authError) {
      setError(authError.message);
    } else if (suspended) {
      setError('Your account has been suspended. Please contact support.');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Economic Club of San Antonio" 
            className="h-20 w-auto mx-auto mb-6"
          />
          <p className="text-gray-400">Sign in to access exclusive events and networking</p>
        </div>

        {/* Login Card */}
        <div className="glass-card rounded-3xl p-8">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Welcome Back</h2>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>
            </div>
          )}

          <form onSubmit={handleSignIn} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-4 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold transition-all duration-300"
                data-testid="email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Password</label>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-4 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold transition-all duration-300"
                data-testid="password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 btn-primary rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:transform-none"
              data-testid="signin-submit-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-black/30 border-t-black" />
              ) : (
                <>Sign In <ArrowRight size={18} /></>
              )}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-brand-gold/20"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-brand-dark-light text-gray-500">New member?</span>
            </div>
          </div>

          <button
            onClick={() => navigate('/application')}
            className="w-full py-4 btn-secondary rounded-xl flex items-center justify-center gap-2"
            data-testid="apply-membership-btn"
          >
            Apply for Membership
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-gray-500 hover:text-gray-300 transition-colors text-sm flex items-center justify-center gap-2"
            data-testid="guest-preview-btn"
          >
            Preview as Guest <ArrowRight size={14} />
          </button>
        </div>

        {/* Disclaimer */}
        <div className="text-center text-xs text-gray-600 px-4 mt-8">
          <p className="font-semibold text-brand-gold/70 mb-2">Important Disclosures</p>
          <p className="leading-relaxed">
            The Economic Club of San Antonio is a private social and educational organization. 
            All content, discussions, and materials are provided for informational purposes only.
          </p>
        </div>
      </div>
    </div>
  );
}
